import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from '../config/dayjs';
import { useCheque } from '../context';

const CheckPaidPage = () => {
  const navigate = useNavigate();
  const { cheques, loading, fetchCheques } = useCheque();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cargar cheques al montar
  useEffect(() => {
    fetchCheques();
  }, [fetchCheques]);

  // Filtrar solo cheques depositados (pagados)
  const chequesPagados = useMemo(() => {
    return cheques.filter(cheque => cheque.estado === 'depositado');
  }, [cheques]);

  // Paginación
  const paginatedCheques = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return chequesPagados.slice(startIndex, startIndex + rowsPerPage);
  }, [chequesPagados, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (chequeId) => {
    navigate(`/cheques/${chequeId}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return '-';
    }
  };

  const getClienteName = (cheque) => {
    if (typeof cheque.cliente === 'object' && cheque.cliente?.nombre) {
      return cheque.cliente.nombre;
    }
    return '-';
  };

  const getBancoNombre = (cheque) => {
    if (typeof cheque.banco === 'object' && cheque.banco?.nombre) {
      return cheque.banco.nombre;
    }
    return '-';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">
          Cheques Pagados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Cheques que ya han sido depositados
        </Typography>
      </Box>

      {/* Tabla de cheques */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Número de Cheque</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell>Fecha de Pago</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedCheques.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No se encontraron cheques pagados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCheques.map((cheque, index) => (
                  <TableRow
                    key={cheque.id}
                    hover
                    onClick={() => handleRowClick(cheque.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {cheque.numero_cheque}
                      </Typography>
                    </TableCell>
                    <TableCell>{getClienteName(cheque)}</TableCell>
                    <TableCell>{getBancoNombre(cheque)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(cheque.monto)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(cheque.fecha_pago)}</TableCell>
                    <TableCell>
                      <Chip
                        label="Depositado"
                        color="info"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={chequesPagados.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Card>
    </Box>
  );
};

export default CheckPaidPage;
